/**
 * WorkOrderWorkflow - Full-screen overlay for the 3-step work order completion flow.
 *
 * This is the main orchestrator component that renders the workflow as a
 * full-screen takeover. It manages navigation between the three steps:
 *   1. Checklist — tech checks off tasks (multi-user, real-time polling)
 *   2. Verify   — primary tech confirms completion + takes photos
 *   3. Sign Off — primary tech provides signature and submits
 *
 * The overlay includes:
 *   - A top bar with step indicator, work order title, a Job Details
 *     button, and a close (X) button
 *   - Step content area that fills the remaining screen height
 *   - A Job Details panel (WorkflowJobDetails) over that content, where the
 *     crew adds people, writes job notes, and looks up the address /
 *     contact / schedule. It is reachable at every step, because the
 *     overlay hides the detail page behind it and there is otherwise
 *     nowhere else to go for any of that.
 *   - Loading and error states
 *   - Offline support via IndexedDB caching
 *
 * Users can close the workflow at any time and return later — progress
 * is preserved via the hook's IndexedDB snapshot caching.
 *
 * @example
 * <WorkOrderWorkflow
 *     isOpen={showWorkflow}
 *     onClose={() => setShowWorkflow(false)}
 *     workOrderId="abc-123"
 *     workOrderTitle="Floor Cleaning — Building A"
 *     currentUserId={user.id}
 * />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkOrderWorkflow, WorkflowStep } from '../../../hooks/useWorkOrderWorkflow';
import { useAuth } from '../../../hooks/useAuth';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { Spinner } from '../../atoms/Spinner';
import WorkflowChecklist from './WorkflowChecklist';
import WorkflowVerify from './WorkflowVerify';
import WorkflowSignOff from './WorkflowSignOff';
import WorkflowJobDetails from './WorkflowJobDetails';
import type { WorkOrder } from '../../../services/workOrderService';
import { WorkflowPhotoCaptureModal } from '../WorkflowPhotoCaptureModal';
import type { WorkflowPhotoCapturePayload } from '../WorkflowPhotoCaptureModal';
import { PhotoLightbox } from '../PhotoLightbox';
import { ConfirmModal } from '../ConfirmModal';
import { uploadService } from '../../../services/uploadService';
import { capturedPhotoToFile } from '../PhotoCapture';
import { usePhotoUploadQueue } from '../../../hooks/usePhotoUploadQueue';
import { PhotoUploadStatusBar } from '../../molecules/PhotoUploadStatusBar';
import type { SlotUploadStatuses, PhotoSlot } from '../../molecules/ChecklistItemPhotoActions';
import { RecipientList, reachableRecipients } from '../../molecules/RecipientList';
import type { Recipient } from '../../molecules/RecipientList';
import { photoTypeService } from '../../../services/photoTypeService';
import { isItemPhotoSatisfied, checklistService } from '../../../services/checklistService';
import { workOrderService } from '../../../services/workOrderService';
import { HoursWorkedFieldset, peopleMissingHours } from '../../molecules/HoursWorkedFieldset';
import { QuickAddUserLink } from '../../molecules/QuickAddUserLink';
import type { HoursWorkedPerson } from '../../molecules/HoursWorkedFieldset';
import { readAssigneeHours, sameHours } from '../../../utils/assigneeHours';
import { formatPersonName } from '../../../utils/formatLabel';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import type { CapturedPhotoPhases } from '../../../services/checklistService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/* The recipient shape is shared with the detail page's "Email Contact to
 * Verify" action, so it lives with the molecule that renders it. Re-exported
 * here because callers of this overlay import it alongside the props. */
export type { Recipient };

export interface WorkOrderWorkflowProps {
    /** Whether the workflow overlay is currently visible */
    isOpen: boolean;
    /** Callback to close the overlay */
    onClose: () => void;
    /** The work order ID to load the checklist for */
    workOrderId: string;
    /** Display title shown in the top bar */
    workOrderTitle: string;
    /** The logged-in user's ID */
    currentUserId: string;
    /**
     * Customer company id this WO belongs to. Used to resolve the
     * default photo_type for per-item photo capture (each company
     * has its own photo taxonomy seeded with "Before" / "After"
     * defaults). Optional so existing callers compile unchanged —
     * when omitted, the per-item camera button is hidden because
     * we have no way to upload without a photo type.
     */
    companyId?: string | null;
    /**
     * Whether the on-site contact IS the assigned technician
     * (contact.user_id === assignee_id). This is the ONLY case where the
     * in-app Verify + Sign Off steps apply — the assignee verifies and
     * signs off their own work. When false (the default), the assignee
     * only completes the Checklist; the separate on-site contact is then
     * notified to verify + sign off, so the stepper shows just "Checklist"
     * and completion lands on a thank-you confirmation.
     */
    contactIsAssignee?: boolean;
    /** Display name of the on-site contact — shown on the post-checklist
     *  confirmation ("<name> has been notified for verification"). */
    onSiteContactName?: string | null;
    /**
     * Everyone the submit-for-verification email would reach. Drives the
     * pre-submit confirmation prompt, which names each recipient and their
     * address before anything leaves the building — and lets the tech submit
     * WITHOUT emailing them. Entries without an address are ignored; an empty
     * list turns the prompt into a plain "nobody will be emailed" confirm.
     */
    verificationRecipients?: Recipient[];
    /**
     * The full work order record, when the caller has it. Powers the Job
     * Details panel — its read-only job info and the starting values for the
     * crew + notes it can edit. Omit it (the default) and the Job Details
     * button is hidden, so callers that only have an id still compile and
     * behave exactly as before.
     */
    workOrder?: WorkOrder | null;
    /**
     * Fired after the Job Details panel saves, so the caller can refetch the
     * record. Without it the panel still saves; the overlay just keeps
     * rendering the copy it was handed until the caller refreshes.
     */
    onWorkOrderUpdated?: () => void;
    /** Optional callback fired when the workflow is successfully submitted */
    onComplete?: () => void;
    /** Optional outer className merged onto the root overlay element. */
    className?: string;
}

/* ============================================
 * STEP CONFIGURATION
 * ============================================ */

/** Step metadata for the progress indicator */
const STEPS: { key: WorkflowStep; label: string; shortLabel: string }[] = [
    { key: 'checklist', label: 'Checklist', shortLabel: '1' },
    { key: 'verify', label: 'Verify', shortLabel: '2' },
    { key: 'sign-off', label: 'Sign Off', shortLabel: '3' },
];

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkOrderWorkflow: React.FC<WorkOrderWorkflowProps> = ({
    isOpen,
    onClose,
    workOrderId,
    workOrderTitle,
    currentUserId,
    companyId,
    contactIsAssignee = false,
    onSiteContactName,
    verificationRecipients,
    workOrder,
    onWorkOrderUpdated,
    onComplete,
    className,
}) => {
    // Elevated roles can verify + sign off on behalf of the team
    // without being the primary assignee. Computed at the parent
    // level so the hook stays decoupled from the auth context.
    const { hasAnyRole } = useAuth();
    const currentUserCanActAsPrimary = hasAnyRole([
        'super_admin',
        'admin',
        'company_admin',
    ]);

    // Initialize the workflow hook with all state and actions
    const workflow = useWorkOrderWorkflow(
        workOrderId,
        currentUserId,
        isOpen,
        currentUserCanActAsPrimary
    );

    /* Per-item photo capture state.
     *   capturingFor — null when the modal is closed; populated with
     *     { itemId, phase } when the user taps a camera CTA. Phase is
     *     undefined for single-photo items and 'before' / 'after' for
     *     before_after items so the upload handler knows which slot
     *     to fill.
     *   capturedPhotosByItem — local per-item record of which slots
     *     the user has filled this session. Lets the "Done" badge
     *     flip optimistically before the parent refetch lands and
     *     gates the per-item required-photo check downstream. */
    const [capturingFor, setCapturingFor] = useState<
        { itemId: string; phase?: 'before' | 'after' } | null
    >(null);
    const [capturedPhotosByItem, setCapturedPhotosByItem] = useState<
        Map<string, CapturedPhotoPhases>
    >(new Map());

    /* Job Details panel visibility. Opened from the header button, closed by
       its own back arrow or by Escape — see the key handler below, which
       peels this off first so Escape never dumps the tech out of the whole
       workflow when they only meant to close the panel. */
    const [showJobDetails, setShowJobDetails] = useState(false);

    /* Background upload queue for per-item photos. Saving in the capture
       modal enqueues and closes immediately; the bytes go up while the tech
       moves to the next checklist item. `meta` records which slot the photo
       belongs to so the camera column can show its state. */
    const photoUploads = usePhotoUploadQueue<{ itemId: string; slot: PhotoSlot }>();

    /* Fold the flat queue into the per-item shape the checklist wants. The
       last entry for a slot wins, which is the right call when a tech
       retakes a photo before the first one has finished uploading. */
    const uploadStatusByItem = useMemo(() => {
        const byItem = new Map<string, SlotUploadStatuses>();
        photoUploads.items.forEach((entry) => {
            const existing = byItem.get(entry.meta.itemId) ?? {};
            byItem.set(entry.meta.itemId, { ...existing, [entry.meta.slot]: entry.status });
        });
        return byItem;
    }, [photoUploads.items]);

    /** Retry every stalled upload — wired to the status bar's "Retry all". */
    const handleRetryAllUploads = useCallback(() => {
        photoUploads.items
            .filter((entry) => entry.status === 'failed' || entry.status === 'waiting')
            .forEach((entry) => photoUploads.retry(entry.id));
    }, [photoUploads]);

    /* "Alert The Primary Tech" CTA state.
     *   alertSending — request is in flight; disables the button so a
     *     jittery tech can't queue up duplicates.
     *   alertSent    — request returned successfully this session; the
     *     button collapses into a confirmation row so the primary tech
     *     doesn't get spammed.
     * Note: the actual delivery channel (in-app feed, PWA push, SMS)
     * isn't picked yet — see PR notes. The handler currently performs
     * an optimistic UI flip so the affordance is live; the real ping
     * is wired up in a follow-up. */
    const [alertSending, setAlertSending] = useState(false);
    const [alertSent, setAlertSent] = useState(false);

    /* Submit-for-verification state (non-contact-assignee flow). When the
     * assignee finishes the checklist and there's a SEPARATE on-site contact,
     * completing the checklist hands off to that contact for verification +
     * sign-off rather than advancing to the in-app Verify/Sign Off steps.
     *   verifySubmitting — request in flight (disables the button).
     *   verifySubmitted  — success this session; swaps the checklist view for
     *     the thank-you confirmation. */
    const [verifySubmitting, setVerifySubmitting] = useState(false);
    const [verifySubmitted, setVerifySubmitted] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);

    /* Pre-submit confirmation prompt. Completing the checklist used to email
     * the customer on the spot; now the tech is shown who would be emailed and
     * picks. `confirmNotifyOpen` drives the dialog, `contactWasEmailed` records
     * which way they answered so the thank-you screen tells the truth instead
     * of always claiming the contact was notified. */
    const [confirmNotifyOpen, setConfirmNotifyOpen] = useState(false);
    const [contactWasEmailed, setContactWasEmailed] = useState(false);

    /* Recipients we can actually reach. Same rule the list itself renders by,
     * so the button labels and the names under them can't disagree. */
    const emailRecipients = useMemo(
        () => reachableRecipients(verificationRecipients),
        [verificationRecipients],
    );

    /* Steps shown in the progress indicator. Verify + Sign Off only apply
     * when the on-site contact is also the assignee; otherwise the assignee's
     * flow is the Checklist alone (the contact verifies + signs off later). */
    const visibleSteps = contactIsAssignee ? STEPS : STEPS.slice(0, 1);

    /* Lightbox state — null when closed; populated with the photo URL +
     * optional metadata when the user taps the expand icon on a
     * thumbnail. The lightbox lives at this level (not inside
     * WorkflowChecklist) so it can layer above the workflow's z-[55]
     * full-screen overlay. */
    const [lightbox, setLightbox] = useState<{ url: string; caption: string | null; area: string | null } | null>(null);

    /**
     * Save handler for per-item photo capture.
     *
     * Resolves nothing and uploads nothing itself — it hands the work to the
     * background queue and returns, which is what lets the capture modal
     * close the instant the tech taps Save. On a site with bad signal the
     * upload might take thirty seconds; the tech shouldn't be standing there
     * for it, and the next four photos shouldn't be queued behind it.
     *
     * Inside the background task the photo_type is resolved by phase:
     *   - single-photo or 'after' → the 'After' seed (per-item checklist
     *     photos default to "documentation of completion" semantics)
     *   - 'before' phase          → the 'Before' seed
     *
     * The slot flag flips optimistically here so the camera CTA responds
     * immediately; the CTA's label reads the queue's status, so it says
     * "Sending" until the server actually has the photo rather than
     * claiming "Done" up front.
     */
    const handlePerItemPhotoSave = async (payload: WorkflowPhotoCapturePayload) => {
        if (!capturingFor || !companyId) {
            throw new Error('Missing context for upload.');
        }
        const { itemId, phase } = capturingFor;
        const slot: PhotoSlot = phase ?? 'single';

        photoUploads.enqueue({
            previewUrl: payload.photo.base64,
            meta: { itemId, slot },
            upload: async () => {
                // Pick the seeded photo_type: 'before' captures get the
                // "Before" type; single-photo + 'after' captures get "After"
                // (documenting-completion is the default semantic).
                const photoTypeName = phase === 'before' ? 'Before' : 'After';
                const photoTypeId = await photoTypeService.resolveByName(String(companyId), photoTypeName);
                if (!photoTypeId) {
                    throw new Error(
                        `No "${photoTypeName}" photo type configured for this company. Ask a Super Admin to seed it in the CMS.`,
                    );
                }

                const file = await capturedPhotoToFile(payload.photo);

                return uploadService.uploadWorkOrderPhoto(workOrderId, file, {
                    photoTypeId,
                    checklistItemId: Number(itemId),
                    photoPhase: phase ?? null,
                    area: payload.area || undefined,
                    caption: payload.details || undefined,
                });
            },
            /* Pull the persisted photo so the thumbnails strip renders it. */
            onUploaded: () => workflow.refresh(),
        });

        // Flip the slot flag for this item so the camera CTA reacts right
        // away; its label still tracks the upload's real state.
        setCapturedPhotosByItem((prev) => {
            const next = new Map(prev);
            const existing = next.get(itemId) ?? {};
            next.set(itemId, { ...existing, [slot as keyof CapturedPhotoPhases]: true });
            return next;
        });
    };

    /**
     * Handler for the "Alert The Primary Tech" CTA shown to non-primary
     * techs once every item is checked off. Today this performs an
     * optimistic UI flip only — the backend notification endpoint is
     * the next thing to wire up (channel TBD: in-app feed vs PWA push
     * vs SMS). Centralized here so the eventual swap to a real service
     * call is a one-line change without touching WorkflowChecklist. */
    const handleAlertPrimary = async () => {
        if (alertSending || alertSent) return;
        setAlertSending(true);
        try {
            // TODO: replace with a real delivery call, e.g.
            //   await checklistService.notifyPrimaryContact(workOrderId);
            // once the notification channel is chosen.
            setAlertSent(true);
        } finally {
            setAlertSending(false);
        }
    };

    /* ============================================
     * HOURS WORKED — asked at hand-off, on every work order
     * ============================================
     * "How long did this take?", per person: the actual-hours half of
     * estimate-versus-actual. The estimate is the job's hour bucket; the
     * answers land on each person's pivot row and roll up into the job's
     * total. Asked at both hand-offs — the sign-off step when the on-site
     * contact is the assignee, the submit-for-verification confirm otherwise
     * — and required only on an hour-tracked job. The crew and any hours
     * already on the record come off the work order's assignees pivot,
     * primary first; with no record in hand there is no crew to ask about,
     * so the prompt stays hidden. */
    const storedCrew = useMemo<HoursWorkedPerson[]>(() => {
        const isPrimary = (a: { pivot?: { isPrimary?: boolean | number } }) => Number(!!Number(a.pivot?.isPrimary ?? 0));
        return [...(workOrder?.assignees ?? [])]
            .sort((a, b) => isPrimary(b) - isPrimary(a))
            .map((a) => ({ id: String(a.id), name: formatPersonName(a) ?? `User #${a.id}` }));
    }, [workOrder]);

    /* People added through "Quick Add User" on the hours prompt. They are
       not on the record yet — they join it with the hours save below — so
       they ride alongside the stored crew until then. */
    const [addedCrew, setAddedCrew] = useState<HoursWorkedPerson[]>([]);
    const newCrew = useMemo(
        () => addedCrew.filter((p) => !storedCrew.some((s) => s.id === p.id)),
        [addedCrew, storedCrew],
    );
    const crew = useMemo(() => [...storedCrew, ...newCrew], [storedCrew, newCrew]);
    const handleQuickAdded = useCallback((people: HoursWorkedPerson[]) => {
        setAddedCrew((prev) => [...prev, ...people.filter((p) => !prev.some((q) => q.id === p.id))]);
    }, []);
    /* The link itself — hidden for roles that can't create users, and only
       once the work order (and so its business) is loaded. */
    const quickAdd = workOrder
        ? <QuickAddUserLink defaultCompanyId={workOrder.companyId} onCreated={handleQuickAdded} />
        : undefined;
    const storedHours = useMemo(() => (workOrder ? readAssigneeHours(workOrder) : {}), [workOrder]);
    const hoursRequired = Boolean(workOrder?.requiresHours);
    const estimatedHours = workOrder?.allocatedHours != null && Number(workOrder.allocatedHours) > 0
        ? Number(workOrder.allocatedHours)
        : null;

    /**
     * Write the hours the crew confirmed, when they differ from the record.
     * Untouched hours skip the request entirely — which also keeps today's
     * offline sign-off working: a queued sign-off needs no PATCH, and only a
     * CHANGE needs a connection. Sends assignee_hours alone; the server keeps
     * the crew as it is on an hours-only save.
     */
    const saveHoursWorked = useCallback(async (hours?: Record<string, string>) => {
        const crewChanged = newCrew.length > 0;
        if (!workOrder || !hours || (sameHours(hours, storedHours) && !crewChanged)) return;
        const entries = Object.entries(hours).filter(([, v]) => v !== '' && v != null);
        /* Quick-added people make this a crew change too: send the full
           additional crew (everyone stored who isn't primary, plus the new
           people) so the server puts them on the job with their hours. */
        const primaryId = workOrder.assigneeId != null ? String(workOrder.assigneeId) : null;
        const additionalAssigneeIds = crewChanged
            ? [...storedCrew.map((p) => p.id).filter((id) => id !== primaryId), ...newCrew.map((p) => p.id)]
            : undefined;
        await workOrderService.update(workOrder.id, {
            assigneeHours: Object.fromEntries(entries.map(([uid, v]) => [uid, Number(v)])),
            ...(additionalAssigneeIds ? { additionalAssigneeIds } : {}),
        });
        setAddedCrew([]);
        onWorkOrderUpdated?.();
    }, [workOrder, storedHours, storedCrew, newCrew, onWorkOrderUpdated]);

    /* Sign-off path: hours first, then the signature. A failed hours save
       stops there, with the error in the step, so the sign-off is never
       queued against hours that did not land. */
    const [hoursError, setHoursError] = useState<string | null>(null);
    const [savingHours, setSavingHours] = useState(false);
    const submitSignOff = workflow.submitSignOff;
    const handleSignOff = useCallback(async (signatureDataUrl: string, notes?: string, hours?: Record<string, string>) => {
        setHoursError(null);
        setSavingHours(true);
        try {
            await saveHoursWorked(hours);
        } catch (err) {
            setHoursError(apiErrorMessage(err, 'Could not save the hours worked. Check your connection and try again.'));
            return;
        } finally {
            setSavingHours(false);
        }
        await submitSignOff(signatureDataUrl, notes);
    }, [saveHoursWorked, submitSignOff]);

    /* Hand-off path: the same prompt inside the submit confirm. Seeded from
       the record each time the dialog opens. */
    const [handOffHours, setHandOffHours] = useState<Record<string, string>>({});
    const handOffMissing = hoursRequired ? peopleMissingHours(crew, handOffHours) : [];
    const openHandOffPrompt = () => {
        setHandOffHours(storedHours);
        setVerifyError(null);
        setConfirmNotifyOpen(true);
    };

    /**
     * Handler for completing the checklist when the on-site contact is a
     * SEPARATE person from the assignee. Hands the work off for verification:
     * the server moves the WO into the in_review state and — only when
     * `notifyContact` is true — emails the on-site contact to verify and sign.
     * Either way the managers' review request goes out and we swap in the
     * thank-you confirmation. No signature is captured here — the contact
     * signs off later, out of band.
     *
     * Called from the confirmation prompt's two answers, never straight from
     * the Submit button, so the customer email is always a deliberate choice.
     *
     * On failure the prompt stays open with the error inside it: the tech is
     * mid-decision, and dumping them back to the checklist would lose that.
     */
    const handleSubmitForVerification = async (notifyContact: boolean) => {
        if (verifySubmitting || verifySubmitted) return;
        /* Hours first. An hour-tracked job cannot leave the tech's hands
           without a number for everyone (the server refuses it too); naming
           who is missing here saves the round-trip. */
        if (handOffMissing.length > 0) {
            setVerifyError(`Record hours for everyone before submitting — still missing: ${handOffMissing.join(', ')}.`);
            return;
        }
        setVerifySubmitting(true);
        setVerifyError(null);
        try {
            await saveHoursWorked(handOffHours);
            await checklistService.submitForVerification(workOrderId, { notifyContact });
            /* Only claim an email went out if we both asked for one and had
             * somewhere to send it. */
            setContactWasEmailed(notifyContact && emailRecipients.length > 0);
            setConfirmNotifyOpen(false);
            setVerifySubmitted(true);
        } catch (err) {
            setVerifyError(err instanceof Error ? err.message : 'Could not submit for verification. Please try again.');
        } finally {
            setVerifySubmitting(false);
        }
    };

    /* Resolve the primary assignee's display name so we can personalize
     * the CTA and confirmation copy ("Jane Doe has been alerted"). Falls
     * through to a generic label when no primary is assigned — the CTA
     * itself is still gated upstream on canActAsPrimary=false so the
     * button only shows when *someone else* needs to be alerted. */
    const primaryContactName =
        workflow.checklist?.assignees.find((a) => a.isPrimary)?.name ?? null;

    // Lock background scroll while the overlay is open (shared,
    // stack-safe, iOS-proof — see useBodyScrollLock).
    useBodyScrollLock(isOpen);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            /* Innermost layer first: the Job Details panel is a surface ON
               the workflow, so Escape closes it and leaves the tech on the
               checklist rather than closing everything. */
            if (showJobDetails) {
                setShowJobDetails(false);
                return;
            }
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, showJobDetails]);

    // Fire onComplete callback when the workflow is submitted
    useEffect(() => {
        if (workflow.checklist?.isSubmitted && onComplete) {
            onComplete();
        }
    }, [workflow.checklist?.isSubmitted, onComplete]);

    // Don't render when closed
    if (!isOpen) return null;

    // Determine the current step index for the progress indicator. Clamped to
    // the visible steps (Verify/Sign Off are hidden unless the on-site contact
    // is the assignee) so a stray currentStep never breaks the indicator.
    const currentStepIndex = Math.max(
        0,
        visibleSteps.findIndex((s) => s.key === workflow.currentStep),
    );

    return (
        /* Full-screen overlay — z-[55]: above the phone BottomTabBar (z-50),
           which used to draw over the checklist's Continue button, and below
           every Modal (z-[60]+) opened from inside it. */
        <div
            className={['work-order-workflow', 'fixed inset-0 z-[55] flex flex-col bg-white', className ?? ''].join(' ')}
            role="dialog"
            aria-modal="true"
            aria-label={`Work Order Workflow: ${workOrderTitle}`}
            data-ref="organism-work-order-workflow"
        >
            {/* ============================================
              * TOP BAR — Close button, title, step indicator
              * ============================================ */}
            <div className="work-order-workflow__header flex-shrink-0 bg-white border-b border-gray-200">
                {/* Top row: close button and title */}
                <div className="flex items-center justify-between px-16px pt-12px pb-8px">
                    {/* Close (X) button — always visible so user can exit anytime */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-44px h-44px rounded-full flex items-center justify-center text-brand-text-muted hover:text-brand-text hover:bg-gray-100 transition-colors -ml-8px"
                        aria-label="Close workflow"
                    >
                        <svg className="w-24px h-24px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Work order title — truncated on small screens */}
                    <h2 className="text-16px font-sans font-bold text-brand-text truncate flex-1 text-center mx-8px">
                        {workOrderTitle}
                    </h2>

                    {/* Job Details — people, notes, and the job info the tech
                      * came for. Rendered only when the caller handed us the
                      * record; without it the panel would have nothing to
                      * show. Occupies the slot that used to be a bare spacer,
                      * so the title stays optically centred either way. */}
                    {workOrder ? (
                        <button
                            type="button"
                            onClick={() => setShowJobDetails(true)}
                            className="w-44px h-44px rounded-full flex items-center justify-center text-brand-text-muted hover:text-brand-primary hover:bg-gray-100 transition-colors -mr-8px"
                            aria-label="Job details — people, notes, and job info"
                        >
                            <svg className="w-24px h-24px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                            </svg>
                        </button>
                    ) : (
                        /* Spacer to balance the close button width */
                        <div className="w-44px" />
                    )}
                </div>

                {/* Step indicator — shows the visible steps. Just "Checklist"
                  * unless the on-site contact is also the assignee, in which
                  * case the full Checklist → Verify → Sign Off run is shown.
                  * With only one step there's nothing to step through, so we
                  * skip the indicator entirely (no lone "1 / Checklist" badge). */}
                {visibleSteps.length > 1 && (
                <div className="flex items-center justify-center gap-4px px-16px pb-12px">
                    {visibleSteps.map((step, index) => {
                        // Determine step state: completed, active, or upcoming
                        const isCompleted = index < currentStepIndex;
                        const isActive = index === currentStepIndex;

                        return (
                            <React.Fragment key={step.key}>
                                {/* Connecting line between steps */}
                                {index > 0 && (
                                    <div className={[
                                        'flex-1 h-2px max-w-[60px] rounded-full transition-colors',
                                        isCompleted || isActive ? 'bg-brand-primary' : 'bg-gray-200',
                                    ].join(' ')} />
                                )}

                                {/* Step circle + label */}
                                <div className="flex flex-col items-center gap-4px">
                                    <div className={[
                                        'w-32px h-32px rounded-full flex items-center justify-center text-13px font-sans font-bold transition-colors',
                                        isActive
                                            ? 'bg-brand-primary text-white shadow-sm'
                                            : isCompleted
                                                ? 'bg-green-600 text-white'
                                                : 'bg-gray-200 text-gray-500',
                                    ].join(' ')}>
                                        {isCompleted ? (
                                            <svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                            </svg>
                                        ) : (
                                            step.shortLabel
                                        )}
                                    </div>
                                    <span className={[
                                        'text-11px font-sans font-medium',
                                        isActive ? 'text-brand-primary' : isCompleted ? 'text-green-600' : 'text-gray-400',
                                    ].join(' ')}>
                                        {step.label}
                                    </span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Background photo uploads — a single line under the step bar
              * so the tech can see whether their photos have landed without
              * scrolling back through the checklist. Renders nothing when
              * the queue is empty. */}
            <PhotoUploadStatusBar items={photoUploads.items} onRetryAll={handleRetryAllUploads} />

            {/* ============================================
              * STEP CONTENT AREA
              * ============================================ */}
            <div className="work-order-workflow__body flex-1 overflow-hidden flex flex-col">
                {workflow.isLoading ? (
                    /* Loading state */
                    <div className="flex-1 flex flex-col items-center justify-center gap-12px">
                        <Spinner size="lg" />
                        <p className="text-15px font-sans text-brand-text-muted">
                            Loading checklist...
                        </p>
                    </div>
                ) : !workflow.checklist ? (
                    /* Error state — no data available */
                    <div className="flex-1 flex flex-col items-center justify-center gap-12px px-24px">
                        <div className="w-56px h-56px rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-28px h-28px text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                            </svg>
                        </div>
                        <p className="text-15px font-sans text-brand-text-muted text-center">
                            {workflow.error || 'Unable to load checklist data.'}
                        </p>
                        <button
                            type="button"
                            onClick={workflow.refresh}
                            className="inline-flex items-center gap-6px px-16px py-10px text-14px font-sans font-medium text-brand-primary bg-white border border-brand-primary rounded-lg hover:bg-brand-primary/10 min-h-44px transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                ) : verifySubmitted ? (
                    /* Thank-you confirmation — shown after the assignee completes
                     * the checklist when the on-site contact is a separate person.
                     * The copy follows the answer given at the prompt: the contact
                     * was emailed to verify + sign off, or the work order went to
                     * review with nobody outside the team contacted. */
                    <div className="flex-1 flex flex-col items-center justify-center gap-16px px-24px text-center">
                        <div className="w-64px h-64px rounded-full bg-green-100 flex items-center justify-center">
                            <svg className="w-32px h-32px text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                            </svg>
                        </div>
                        <h3 className="text-20px font-sans font-bold text-brand-text">Checklist complete — thank you!</h3>
                        <p className="text-15px font-sans text-brand-text-muted max-w-sm">
                            {contactWasEmailed ? (
                                onSiteContactName ? (
                                    <>
                                        <span className="font-semibold text-brand-text">{onSiteContactName}</span> has been emailed to verify and sign off on this work order.
                                    </>
                                ) : (
                                    'The customer contact has been emailed to verify and sign off on this work order.'
                                )
                            ) : (
                                <>
                                    This work order was sent for review. {onSiteContactName ? (
                                        <><span className="font-semibold text-brand-text">{onSiteContactName}</span> was not emailed.</>
                                    ) : (
                                        'The customer contact was not emailed.'
                                    )}
                                </>
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={() => { if (onComplete) { onComplete(); } else { onClose(); } }}
                            className="inline-flex items-center justify-center px-24px py-12px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    /* Render the active step component */
                    <>
                        {/* Checklist shows on the checklist step, and ALWAYS when
                          * the contact isn't the assignee (that flow has no in-app
                          * Verify/Sign Off steps, so we never leave the checklist). */}
                        {(workflow.currentStep === 'checklist' || !contactIsAssignee) && (
                            <WorkflowChecklist
                                items={workflow.checklist.items}
                                onToggleItem={workflow.toggleItem}
                                /* Lets the crew record work the template never
                                   listed, before the customer signs. */
                                onAddItem={workflow.addItem}
                                allCompleted={
                                    /* Required items must also satisfy their photo
                                     * requirement before the gate releases.
                                     * isItemPhotoSatisfied unions server-persisted
                                     * photos with this-session captures and accounts
                                     * for photoMode (mode='photo' needs one shot,
                                     * mode='before_after' needs both slots). */
                                    workflow.allItemsCompleted &&
                                    workflow.checklist.items.every((it) =>
                                        !it.isRequired || isItemPhotoSatisfied(it, capturedPhotosByItem.get(it.id))
                                    )
                                }
                                canActAsPrimary={workflow.canActAsPrimary}
                                /* When the on-site contact is the assignee, the
                                 * completed checklist advances to the in-app
                                 * Verify step. Otherwise it hands off to the
                                 * separate contact for verification. */
                                mode={contactIsAssignee ? 'verify' : 'notify_contact'}
                                onSiteContactName={onSiteContactName}
                                submitting={verifySubmitting}
                                /* Submit opens the confirmation prompt — the
                                 * actual hand-off happens from one of its two
                                 * answers, never straight off this button. */
                                onContinue={contactIsAssignee ? () => workflow.goToStep('verify') : openHandOffPrompt}
                                isOffline={workflow.isOffline}
                                error={workflow.error || verifyError}
                                /* Only enable the per-item camera CTA when we know the
                                 * company id (needed to resolve the seeded photo_type). */
                                onCapturePhotoForItem={
                                    companyId
                                        ? (itemId, phase) => setCapturingFor({ itemId, phase })
                                        : undefined
                                }
                                capturedPhotosByItem={capturedPhotosByItem}
                                uploadStatusByItem={uploadStatusByItem}
                                onExpandPhoto={(url, meta) => setLightbox({ url, ...meta })}
                                /* "Alert The Primary Tech" CTA props. The button
                                 *  only renders inside WorkflowChecklist when a
                                 *  handler is supplied AND the current user is
                                 *  NOT the primary contact — so a primary tech
                                 *  closing out their own job never sees it. */
                                onAlertPrimary={handleAlertPrimary}
                                primaryContactName={primaryContactName}
                                alertSent={alertSent}
                                alertSending={alertSending}
                            />
                        )}

                        {contactIsAssignee && workflow.currentStep === 'verify' && (
                            <WorkflowVerify
                                items={workflow.checklist.items}
                                verificationPhotos={workflow.verificationPhotos}
                                onAddPhoto={workflow.addVerificationPhoto}
                                onRemovePhoto={workflow.removeVerificationPhoto}
                                onSubmitVerification={workflow.submitVerification}
                                onBack={() => workflow.goToStep('checklist')}
                                isSubmitting={workflow.isSubmitting}
                                error={workflow.error}
                                isOffline={workflow.isOffline}
                            />
                        )}

                        {contactIsAssignee && workflow.currentStep === 'sign-off' && (
                            <WorkflowSignOff
                                checklist={workflow.checklist}
                                /* Hours are saved first, then the signature —
                                   see handleSignOff. */
                                onSubmitSignOff={handleSignOff}
                                onBack={() => workflow.goToStep('verify')}
                                /* Seeded with the job notes written earlier in
                                   the Job Details panel — both edit the same
                                   checklist_notes column. */
                                initialNotes={workOrder?.checklistNotes ?? null}
                                crew={crew}
                                quickAdd={quickAdd}
                                initialHours={storedHours}
                                hoursRequired={hoursRequired}
                                estimatedHours={estimatedHours}
                                isSubmitting={workflow.isSubmitting || savingHours}
                                error={workflow.error || hoursError}
                                isOffline={workflow.isOffline}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Job Details panel — covers the workflow body (and its header)
              * with its own back-arrow header. Sits at z-10 INSIDE this
              * overlay so the capture modal, lightbox, and confirm dialog
              * below still stack above it. */}
            {workOrder && showJobDetails && (
                <WorkflowJobDetails
                    workOrder={workOrder}
                    onClose={() => setShowJobDetails(false)}
                    onSaved={onWorkOrderUpdated}
                />
            )}

            {/* Per-item photo capture modal — opened by the camera CTA
             *  on items with require_photo=true. Owns its own two-step
             *  flow (capture → preview + area/details) and hands the
             *  payload back here for the upload + completion-gate
             *  bookkeeping. */}
            <WorkflowPhotoCaptureModal
                isOpen={capturingFor !== null}
                onClose={() => setCapturingFor(null)}
                onSave={handlePerItemPhotoSave}
                title={
                    capturingFor
                        ? `Photo for: ${
                              workflow.checklist?.items.find((i) => i.id === capturingFor.itemId)?.title ?? 'item'
                          }${capturingFor.phase ? ` — ${capturingFor.phase.toUpperCase()}` : ''}`
                        : 'Add photo'
                }
            />

            {/* Fullscreen photo viewer — opened from the expand icon on
             *  a thumbnail in the checklist. Sits above the workflow's
             *  own z-[55] layer so it covers everything else. */}
            <PhotoLightbox
                isOpen={lightbox !== null}
                imageUrl={lightbox?.url ?? null}
                caption={lightbox?.caption ?? null}
                area={lightbox?.area ?? null}
                onClose={() => setLightbox(null)}
            />

            {/* Pre-submit confirmation — the customer-facing email is never a
              * silent side effect of finishing the checklist. The tech sees
              * exactly who would receive it and answers one of three ways:
              *   Cancel        — back to the checklist, nothing submitted
              *   Don't Email   — submit for review, customer not contacted
              *   Email & Submit— submit for review AND email the contact(s)
              * With no reachable recipient the middle option is dropped and the
              * dialog degrades to a plain "nobody gets emailed" confirm. */}
            <ConfirmModal
                isOpen={confirmNotifyOpen}
                onClose={() => setConfirmNotifyOpen(false)}
                onConfirm={() => handleSubmitForVerification(emailRecipients.length > 0)}
                title="Submit work order"
                confirmLabel={emailRecipients.length > 0 ? 'Email & Submit' : 'Submit'}
                cancelLabel="Cancel"
                secondaryLabel={emailRecipients.length > 0 ? "Don't Email" : undefined}
                onSecondary={emailRecipients.length > 0 ? () => handleSubmitForVerification(false) : undefined}
                loading={verifySubmitting}
                confirmDisabled={handOffMissing.length > 0}
            >
                <p className="text-14px font-sans text-brand-text-muted mb-12px">
                    Submitting sends this work order for review. Your managers are notified either way.
                </p>

                {/* "How long did this take?" — per person, on every work
                  * order. Required, and holding the buttons, only when the
                  * job tracks hours. The answer lands on the record before
                  * the hand-off request goes out. */}
                {crew.length > 0 && (
                    <div className="mb-16px">
                        <HoursWorkedFieldset
                            people={crew}
                            hours={handOffHours}
                            onChange={setHandOffHours}
                            required={hoursRequired}
                            estimatedHours={estimatedHours}
                            quickAdd={quickAdd}
                        />
                    </div>
                )}

                {/* Shared with the detail page's reviewer-side prompt so both
                  * answer "who exactly gets this?" the same way. */}
                <RecipientList recipients={emailRecipients} lead="It will also email" purpose="to verify and sign" emptyText="No email address is on file for the on-site contact, so nobody outside the team will be emailed." />

                {emailRecipients.length > 0 && (
                    <p className="mt-12px text-13px font-sans text-brand-text-muted">
                        {'Choose "Don\'t Email" to submit for review without contacting them.'}
                    </p>
                )}

                {/* Failures surface here rather than behind the dialog — the
                  * tech is mid-decision and shouldn't have to hunt for why
                  * nothing happened. */}
                {verifyError && (
                    <p className="mt-12px text-13px font-sans text-red-600" role="alert">{verifyError}</p>
                )}
            </ConfirmModal>
        </div>
    );
};

export default WorkOrderWorkflow;
